import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-file-unknow-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该文章可能已被移除或链接无效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.publishedAt,
          articleSection: article.category,
          wordCount: article.content.length,
        }}
      />

      <Navbar />

      <article className="min-h-screen pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          <header className="pb-8 border-b border-background-200/70">
            <div className="mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-block px-3 py-1 text-xs bg-primary-100 text-primary-700 rounded-full cursor-pointer whitespace-nowrap hover:bg-primary-200 transition-colors"
              >
                {article.category}
              </Link>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-snug mb-4">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span className="whitespace-nowrap">{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span className="whitespace-nowrap">{article.publishedAt}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span className="whitespace-nowrap">阅读时间：{article.readTime}</span>
            </div>
          </header>

          <div className="py-8">
            <div className="w-full rounded-lg overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                [&_h3]:text-lg [&_h3]:font-heading [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-6 [&_h3]:mb-3
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_ul]:mb-4 [&_ul]:pl-5 [&_ul]:list-disc [&_li]:mb-1.5
                [&_ol]:mb-4 [&_ol]:pl-5 [&_ol]:list-decimal [&_li]:mb-1.5
                [&_strong]:text-foreground-900 [&_strong]:font-semibold"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="pb-14 md:pb-20">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-40 overflow-hidden">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {rel.title}
                      </h3>
                      <span className="text-xs text-foreground-400">{rel.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <Footer />
      <ScrollToTop />
    </>
  );
}